import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../api';

const DIAS_SEMANA = [
  { v: 0, label: 'Domingo' },
  { v: 1, label: 'Lunes' },
  { v: 2, label: 'Martes' },
  { v: 3, label: 'Miércoles' },
  { v: 4, label: 'Jueves' },
  { v: 5, label: 'Viernes' },
  { v: 6, label: 'Sábado' }
];

const CHECKLIST_VALIDACION = [
  'Dataset piloto (5–10 empresas) revisado contra propuesta F29 del SII',
  'Códigos 504 / 562 / 142 / 89 conciliados o documentada la diferencia',
  'Remanente y UTM validados con contador responsable',
  'Correo de prueba recibido (logo, PDF, Reply-To contador IVA)',
  'Ningún AUTO_SEND activo hasta cierre formal del checklist'
];

function fmtDigestHorario(cfg) {
  const d = DIAS_SEMANA.find((x) => x.v === Number(cfg.digestCronDayOfWeek ?? 1));
  const h = String(cfg.digestCronHour ?? 8).padStart(2, '0');
  const m = String(cfg.digestCronMinute ?? 0).padStart(2, '0');
  return `${d?.label || '—'} ${h}:${m}`;
}

export default function InformeIvaAdministracionPage() {
  const now = new Date();
  const [mesKpi, setMesKpi] = useState(now.getMonth() + 1);
  const [anioKpi, setAnioKpi] = useState(now.getFullYear());
  const [cfg, setCfg] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checklist, setChecklist] = useState(() => {
    try {
      const raw = localStorage.getItem('informeIvaChecklistValidacion');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const loadCfg = useCallback(async () => {
    const { data } = await api.get('/informes-iva/config/global');
    setCfg(data);
  }, []);

  const loadResumen = useCallback(async () => {
    const { data } = await api.get('/informes-iva/administracion/resumen', {
      params: { mes: mesKpi, anio: anioKpi }
    });
    setResumen(data);
  }, [mesKpi, anioKpi]);

  useEffect(() => {
    loadCfg().catch((e) => toast.error(e.response?.data?.error || e.message));
  }, [loadCfg]);

  useEffect(() => {
    loadResumen().catch(() => setResumen(null));
  }, [loadResumen]);

  const toggleChecklist = (item) => {
    setChecklist((prev) => {
      const next = { ...prev, [item]: !prev[item] };
      localStorage.setItem('informeIvaChecklistValidacion', JSON.stringify(next));
      return next;
    });
  };

  const saveCfg = async () => {
    if (!cfg) return;
    if (cfg.autoSendGlobalEnabled && !window.confirm('¿Activar AUTO_SEND global? Solo tras validación tributaria completa.')) {
      return;
    }
    if (cfg.modoOperacion === 'PRODUCCION' && !window.confirm('¿Pasar a PRODUCCIÓN? Se permitirán envíos reales a clientes.')) {
      return;
    }
    const ini = Number(cfg.periodoIvaDiaInicio ?? 4);
    const fin = Number(cfg.periodoIvaDiaCierre ?? 18);
    if (fin <= ini) {
      toast.error('El día de cierre debe ser posterior al día de inicio');
      return;
    }
    setBusy(true);
    try {
      await api.put('/informes-iva/config/global', {
        modoOperacion: cfg.modoOperacion || 'PRUEBA',
        digestMode: cfg.digestMode,
        digestPorContadorEnabled: cfg.digestPorContadorEnabled,
        digestDestinoPruebaEmail: cfg.digestDestinoPruebaEmail,
        digestScheduleEnabled: cfg.digestScheduleEnabled,
        digestCronDayOfWeek: cfg.digestCronDayOfWeek,
        digestCronHour: cfg.digestCronHour,
        digestCronMinute: cfg.digestCronMinute,
        scheduledCronHour: cfg.scheduledCronHour,
        scheduledCronMinute: cfg.scheduledCronMinute,
        scheduledMaxEjecucionesMes: cfg.scheduledMaxEjecucionesMes,
        periodoIvaDiaInicio: cfg.periodoIvaDiaInicio,
        periodoIvaDiaCierre: cfg.periodoIvaDiaCierre,
        persistInformeFiles: cfg.persistInformeFiles,
        emailOficina: cfg.emailOficina,
        frescuraHoras: cfg.frescuraHoras,
        timezone: cfg.timezone,
        retencionDias: cfg.retencionDias,
        autoSyncOnRun: cfg.autoSyncOnRun,
        syncF29OnRun: cfg.syncF29OnRun,
        scheduledRunsEnabled: cfg.scheduledRunsEnabled,
        autoSendGlobalEnabled: cfg.autoSendGlobalEnabled,
        permitirRemanenteCeroAsumido: cfg.permitirRemanenteCeroAsumido
      });
      toast.success('Configuración guardada');
      await loadCfg();
      await loadResumen();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const enviarDigest = async () => {
    const prueba = (cfg?.modoOperacion || 'PRUEBA') !== 'PRODUCCION' || !cfg?.digestPorContadorEnabled;
    const msg = prueba
      ? `¿Enviar digest de PRUEBA a ${resumen?.digestPruebaTo || 'super admin'}?`
      : '¿Enviar digest de PRODUCCIÓN a contadores/oficina según configuración?';
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/informes-iva/digest/send/${anioKpi}/${mesKpi}`);
      toast.success(`Digest enviado (${data.mode || '—'}, OK: ${data.sent ?? 0})`);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const ejecutarCron = async () => {
    if (!window.confirm('¿Encolar informes automáticos ahora? Respeta ventana del mes y frecuencia por cliente.')) return;
    setBusy(true);
    try {
      const { data } = await api.post('/informes-iva/jobs/scheduled-run');
      if (data.skipped) {
        toast.message(`Cron omitido: ${data.skipped}`);
      } else {
        toast.success(`${data.enqueued ?? 0} informe(s) encolados`);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) {
    return <div className="max-w-4xl mx-auto p-8 text-slate-500">Cargando administración…</div>;
  }

  const modoPrueba = (cfg.modoOperacion || 'PRUEBA') !== 'PRODUCCION';
  const digestProduccionActivo = !modoPrueba && Boolean(cfg.digestPorContadorEnabled);
  const ventana = resumen?.ventanaPeriodo;

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 space-y-8">
      <div>
        <p className="text-sm text-slate-500">
          <Link to="/informes-iva" className="text-indigo-600 hover:underline">
            ← Panel del mes
          </Link>
        </p>
        <h1 className="text-2xl font-bold text-slate-900 mt-1">Administración Informes IVA</h1>
        <p className="text-sm text-slate-600 mt-2">
          Crons, ambiente piloto/producción y reglas globales. El historial útil está en cada empresa (botón Historial en el panel).
        </p>
      </div>

      <section
        className={`rounded-xl border-2 p-5 ${modoPrueba ? 'bg-amber-50 border-amber-400' : 'bg-emerald-50 border-emerald-500'}`}
      >
        <p className={`text-lg font-bold ${modoPrueba ? 'text-amber-950' : 'text-emerald-950'}`}>
          {modoPrueba ? 'MODO PRUEBA' : 'MODO PRODUCCIÓN'}
        </p>
        <ul className="text-sm mt-2 text-slate-800 list-disc pl-5 space-y-1">
          {modoPrueba ? (
            <>
              <li>No se envían correos con informe al cliente.</li>
              <li>El digest semanal solo va al correo de prueba (bloque amarillo abajo).</li>
            </>
          ) : (
            <>
              <li>Permitidos envíos al cliente (según aprobación y modo de envío por empresa).</li>
              <li>El digest puede ir a contadores/oficina si lo activa en el bloque verde.</li>
            </>
          )}
        </ul>
        <label className="block mt-4 max-w-xs text-sm font-medium">
          Cambiar ambiente
          <select
            className="w-full border rounded-lg px-3 py-2 mt-1 bg-white"
            value={cfg.modoOperacion || 'PRUEBA'}
            onChange={(e) => setCfg({ ...cfg, modoOperacion: e.target.value })}
          >
            <option value="PRUEBA">PRUEBA (piloto)</option>
            <option value="PRODUCCION">PRODUCCIÓN</option>
          </select>
        </label>
      </section>

      {resumen && (
        <section className="bg-white border border-slate-200 rounded-xl p-4 text-sm space-y-3">
          <div className="flex flex-wrap gap-3 items-end">
            <label className="text-xs">
              KPIs del mes
              <select className="block border rounded-lg px-2 py-1.5 mt-1" value={mesKpi} onChange={(e) => setMesKpi(Number(e.target.value))}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs">
              Año
              <input type="number" className="block border rounded-lg px-2 py-1.5 mt-1 w-24" value={anioKpi} onChange={(e) => setAnioKpi(Number(e.target.value))} />
            </label>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-slate-50 p-2">
              <p className="text-xs text-slate-500">Enviados</p>
              <p className="font-semibold">{resumen.informesEnviadosMes}</p>
            </div>
            <div className="rounded-lg bg-red-50 p-2">
              <p className="text-xs text-slate-500">Fallidos</p>
              <p className="font-semibold text-red-800">{resumen.entregasFallidasMes}</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-2">
              <p className="text-xs text-slate-500">Pend. aprobación</p>
              <p className="font-semibold">{resumen.pendientesAprobacionMes}</p>
            </div>
          </div>
          {ventana?.mensaje && (
            <p className={`text-xs p-3 rounded-lg ${ventana.skipScheduledCron ? 'bg-slate-100 text-slate-700' : 'bg-indigo-50 text-indigo-950'}`}>
              {ventana.mensaje}
            </p>
          )}
        </section>
      )}

      <section className="space-y-4">
        <h2 className="font-semibold text-slate-900">Correo digest (resumen semanal)</h2>
        <p className="text-sm text-slate-600">
          Un mail con empresas que requieren atención. Horario programado:{' '}
          <strong>{fmtDigestHorario(cfg)}</strong> (zona {cfg.timezone || 'America/Santiago'}).
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <div className={`rounded-xl border-2 p-4 space-y-3 ${modoPrueba ? 'border-amber-400 bg-amber-50/80' : 'border-slate-200 bg-slate-50 opacity-90'}`}>
            <p className="font-semibold text-amber-950">Digest en PRUEBA {modoPrueba ? '(activo ahora)' : '(inactivo — está en producción)'}</p>
            <p className="text-xs text-slate-700">
              Asunto <code className="bg-white px-1 rounded">[Prueba]</code>. Un solo destinatario. Los contadores no reciben nada.
            </p>
            <label className="block text-xs">
              Email destino (vacío = super admin)
              <input
                type="email"
                className="w-full border rounded-lg px-3 py-2 mt-1 bg-white"
                value={cfg.digestDestinoPruebaEmail || ''}
                onChange={(e) => setCfg({ ...cfg, digestDestinoPruebaEmail: e.target.value })}
                disabled={!modoPrueba}
              />
            </label>
            {modoPrueba && (
              <p className="text-xs text-amber-900">
                Irá a: <strong>{resumen?.digestPruebaTo || '—'}</strong>
              </p>
            )}
          </div>

          <div
            className={`rounded-xl border-2 p-4 space-y-3 ${digestProduccionActivo ? 'border-emerald-500 bg-emerald-50/80' : 'border-slate-200 bg-white'}`}
          >
            <p className="font-semibold text-emerald-950">
              Digest en PRODUCCIÓN {digestProduccionActivo ? '(activo ahora)' : '(inactivo)'}
            </p>
            <p className="text-xs text-slate-700">Requiere ambiente PRODUCCIÓN y activar envío a contadores/oficina.</p>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={Boolean(cfg.digestPorContadorEnabled)}
                disabled={modoPrueba}
                onChange={(e) => setCfg({ ...cfg, digestPorContadorEnabled: e.target.checked })}
              />
              Enviar a contadores IVA / oficina (no solo prueba)
            </label>
            {modoPrueba && <p className="text-xs text-slate-500">Pase a PRODUCCIÓN arriba para habilitar.</p>}
            {cfg.digestPorContadorEnabled && !modoPrueba && (
              <>
                <label className="block text-xs">
                  Modo
                  <select className="w-full border rounded-lg px-2 py-2 mt-1" value={cfg.digestMode || 'POR_ASIGNADO'} onChange={(e) => setCfg({ ...cfg, digestMode: e.target.value })}>
                    <option value="POR_ASIGNADO">Por contador IVA</option>
                    <option value="GLOBAL_OFICINA">Correo oficina</option>
                    <option value="AMBOS">Ambos</option>
                  </select>
                </label>
                <label className="block text-xs">
                  Email oficina
                  <input type="email" className="w-full border rounded-lg px-2 py-2 mt-1" value={cfg.emailOficina || ''} onChange={(e) => setCfg({ ...cfg, emailOficina: e.target.value })} />
                </label>
              </>
            )}
          </div>
        </div>

        <div className="bg-white border rounded-xl p-4 space-y-3 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={cfg.digestScheduleEnabled !== false} onChange={(e) => setCfg({ ...cfg, digestScheduleEnabled: e.target.checked })} />
            Programar digest automático
          </label>
          <div className="grid sm:grid-cols-3 gap-3 max-w-lg text-xs">
            <label>
              Día
              <select className="w-full border rounded-lg px-2 py-2 mt-1" value={Number(cfg.digestCronDayOfWeek ?? 1)} onChange={(e) => setCfg({ ...cfg, digestCronDayOfWeek: Number(e.target.value) })}>
                {DIAS_SEMANA.map((d) => (
                  <option key={d.v} value={d.v}>
                    {d.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Hora
              <input type="number" min={0} max={23} className="w-full border rounded-lg px-2 py-2 mt-1" value={cfg.digestCronHour ?? 8} onChange={(e) => setCfg({ ...cfg, digestCronHour: Number(e.target.value) })} />
            </label>
            <label>
              Minuto
              <input type="number" min={0} max={59} className="w-full border rounded-lg px-2 py-2 mt-1" value={cfg.digestCronMinute ?? 0} onChange={(e) => setCfg({ ...cfg, digestCronMinute: Number(e.target.value) })} />
            </label>
          </div>
          <button type="button" disabled={busy} onClick={enviarDigest} className="px-4 py-2 border rounded-lg text-sm">
            Enviar digest ahora (manual)
          </button>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4 text-sm">
        <h2 className="font-semibold text-slate-900">Informes automáticos (sync SII + cálculo)</h2>
        <p className="text-slate-600 leading-relaxed">
          Una vez al día, a la hora indicada, el sistema revisa qué empresas toca recalcular según la <strong>frecuencia</strong> de
          cada una en{' '}
          <Link to="/informes-iva/configuracion" className="text-indigo-600 underline">
            Configuración rápida
          </Link>{' '}
          (ej. cada 7 días). No envía correos al cliente salvo AUTO_SEND explícito.
        </p>

        <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-3">
          <p className="font-medium text-slate-800">Ventana del mes (evita chocar inicio vs cierre)</p>
          <div className="grid sm:grid-cols-2 gap-4 text-xs">
            <label>
              Día de inicio (desde aquí puede correr el cron)
              <input type="number" min={1} max={28} className="w-full border rounded-lg px-2 py-2 mt-1" value={cfg.periodoIvaDiaInicio ?? 4} onChange={(e) => setCfg({ ...cfg, periodoIvaDiaInicio: Number(e.target.value) })} />
            </label>
            <label>
              Día de cierre (desde aquí el período objetivo pasa al mes en curso)
              <input type="number" min={2} max={28} className="w-full border rounded-lg px-2 py-2 mt-1" value={cfg.periodoIvaDiaCierre ?? 18} onChange={(e) => setCfg({ ...cfg, periodoIvaDiaCierre: Number(e.target.value) })} />
            </label>
          </div>
          <p className="text-slate-600 text-xs">
            Ejemplo con inicio 4 y cierre 18: días 1–3 no hay corridas; del 4 al 17 se informa el mes anterior; del 18 en adelante, el mes
            calendario vigente.
          </p>
        </div>

        <label className="flex items-center gap-2">
          <input type="checkbox" checked={Boolean(cfg.scheduledRunsEnabled)} onChange={(e) => setCfg({ ...cfg, scheduledRunsEnabled: e.target.checked })} />
          Activar revisiones automáticas diarias
        </label>
        <div className="grid sm:grid-cols-2 gap-3 max-w-md text-xs">
          <label>
            Hora del chequeo (una vez al día)
            <input type="number" min={0} max={23} className="w-full border rounded-lg px-2 py-2 mt-1" value={cfg.scheduledCronHour ?? 6} onChange={(e) => setCfg({ ...cfg, scheduledCronHour: Number(e.target.value) })} />
          </label>
          <label>
            Minuto
            <input type="number" min={0} max={59} className="w-full border rounded-lg px-2 py-2 mt-1" value={cfg.scheduledCronMinute ?? 0} onChange={(e) => setCfg({ ...cfg, scheduledCronMinute: Number(e.target.value) })} />
          </label>
        </div>
        <label className="block max-w-xs text-xs">
          Máximo de corridas automáticas por empresa y mes tributario
          <input type="number" min={1} max={31} className="w-full border rounded-lg px-2 py-2 mt-1" value={cfg.scheduledMaxEjecucionesMes ?? 8} onChange={(e) => setCfg({ ...cfg, scheduledMaxEjecucionesMes: Number(e.target.value) })} />
        </label>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4 text-sm">
        <h2 className="font-semibold text-slate-900">Motor y calidad de datos</h2>
        <details className="text-xs text-slate-600">
          <summary className="cursor-pointer font-medium text-slate-800">¿Qué significan frescura, retención y zona horaria?</summary>
          <ul className="mt-2 list-disc pl-4 space-y-1">
            <li>
              <strong>Frescura (horas):</strong> antigüedad máxima de datos SII para permitir envío al cliente.
            </li>
            <li>
              <strong>Retención snapshots (días):</strong> cuánto tiempo se conservan cálculos en BD (limpieza futura).
            </li>
            <li>
              <strong>Zona horaria:</strong> usada para horarios de cron y ventana del mes (Chile = America/Santiago).
            </li>
          </ul>
        </details>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={Boolean(cfg.persistInformeFiles)} onChange={(e) => setCfg({ ...cfg, persistInformeFiles: e.target.checked })} />
          Guardar HTML/PDF en disco (desactivado = solo BD, PDF al descargar)
        </label>
        <label className="flex items-center gap-2 text-red-900">
          <input type="checkbox" checked={Boolean(cfg.autoSendGlobalEnabled)} onChange={(e) => setCfg({ ...cfg, autoSendGlobalEnabled: e.target.checked })} />
          AUTO_SEND global
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={Boolean(cfg.autoSyncOnRun)} onChange={(e) => setCfg({ ...cfg, autoSyncOnRun: e.target.checked })} />
          Sync SII al pulsar Informe IVA (manual)
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={cfg.syncF29OnRun !== false} onChange={(e) => setCfg({ ...cfg, syncF29OnRun: e.target.checked })} />
          Incluir F29 en sync SII
        </label>
        <div className="grid sm:grid-cols-3 gap-3 text-xs">
          <label>
            Frescura (h)
            <input type="number" className="w-full border rounded-lg px-2 py-2 mt-1" value={cfg.frescuraHoras ?? 48} onChange={(e) => setCfg({ ...cfg, frescuraHoras: Number(e.target.value) })} />
          </label>
          <label>
            Retención (días)
            <input type="number" className="w-full border rounded-lg px-2 py-2 mt-1" value={cfg.retencionDias ?? 365} onChange={(e) => setCfg({ ...cfg, retencionDias: Number(e.target.value) })} />
          </label>
          <label>
            Zona horaria
            <input type="text" className="w-full border rounded-lg px-2 py-2 mt-1" value={cfg.timezone || 'America/Santiago'} onChange={(e) => setCfg({ ...cfg, timezone: e.target.value })} />
          </label>
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          <button type="button" disabled={busy} onClick={saveCfg} className="px-4 py-2 bg-slate-900 text-white rounded-lg disabled:opacity-50">
            Guardar todo
          </button>
          <button type="button" disabled={busy} onClick={ejecutarCron} className="px-4 py-2 border border-slate-300 rounded-lg disabled:opacity-50">
            Probar cron cálculos ahora
          </button>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-3 text-sm">
        <h2 className="font-semibold text-slate-900">Checklist piloto (local)</h2>
        <ul className="space-y-2">
          {CHECKLIST_VALIDACION.map((item) => (
            <label key={item} className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={Boolean(checklist[item])} onChange={() => toggleChecklist(item)} className="mt-0.5" />
              <span className={checklist[item] ? 'text-slate-500 line-through' : 'text-slate-800'}>{item}</span>
            </label>
          ))}
        </ul>
      </section>
    </div>
  );
}
